import type { MessageKey } from "@baton/i18n";
import { useI18n } from "@baton/i18n";
import { useEffect, useLayoutEffect } from "react";
import { useLocation } from "react-router";
import { noteScreenTitle } from "../shell/history/origin.ts";
import { useOptionalWorkspaceContext } from "../shell/layout/WorkspaceShell.tsx";

/**
 * FR-015-007, FR-015-100, `contracts/design-system.md` § Focus and § Names, roles and
 * structure: every route has a document title `{screen} | {workspace}` inside a workspace and
 * `{screen}` outside one, the workspace segment read from the shell's context so no screen can
 * name its project there instead (2026-09-14, by the owner's instruction); one `<h1>` matching
 * the title's leading segment, and a focus target that receives focus
 * on navigation. A screen declares its metadata through `useRouteMeta`; the shell's skip
 * link is the first tab stop and points at the same `#main` region. The screen's name is also noted
 * on its history entry, so the Back of a page opened from it names it (`shell/history/origin.ts`,
 * 2026-10-07).
 */
export interface RouteMeta {
  /** The screen's name, which is also its `<h1>`: a catalogue key, or text the data supplies (an item's identifier). */
  readonly screen: MessageKey | { readonly text: string };
  /** The element that receives focus on arrival; the `<h1>` when the ref is absent. */
  readonly focus?: React.RefObject<HTMLElement | null>;
  /** False while the focus target is not drawn yet, a heading the screen's data draws (the task page's title, 2026-10-03): the focus waits for it rather than going nowhere. */
  readonly ready?: boolean;
}

export const FOCUS_TARGET_ID = "main";

/** What held focus when the person last navigated to a screen that loads on its own, or null before any such navigation. */
let focusedAtNavigation: Element | null = null;

/**
 * Notes what holds focus the moment the location changes, ahead of the screen, which may still be loading its code or its
 * catalogue. The screen's focus target takes focus when it arrives only if focus is still there: a person who opened the account
 * menu or a picker while the screen loaded keeps it (2026-09-29, the Dashboard's heading took focus from the account menu and
 * closed it under the pointer). `routes.tsx` mounts it beside every screen that loads on its own.
 */
export function NavigationFocusMark(): null {
  const location = useLocation();
  useLayoutEffect(() => {
    focusedAtNavigation = document.activeElement;
  }, [location.pathname]);
  return null;
}

export function useRouteMeta(meta: RouteMeta): void {
  const { t } = useI18n();
  const location = useLocation();
  const screen = typeof meta.screen === "string" ? t(meta.screen) : meta.screen.text;
  const context = useOptionalWorkspaceContext();
  const title = context === null ? screen : t("route.title", { screen, workspace: context.workspace?.name ?? context.ws });
  useEffect(() => {
    document.title = title;
  }, [title]);
  useEffect(() => {
    noteScreenTitle(location.key, screen);
  }, [location.key, screen]);
  const focus = meta.focus;
  const ready = meta.ready ?? true;
  useEffect(() => {
    if (!ready) return;
    const marked = focusedAtNavigation;
    focusedAtNavigation = null;
    const active = document.activeElement;
    if (marked !== null && active !== null && active !== document.body && active !== marked && active.isConnected) return;
    const target = focus?.current ?? document.querySelector<HTMLElement>(`#${FOCUS_TARGET_ID} h1`);
    if (target === null) return;
    if (!target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1");
    target.focus({ preventScroll: false });
  }, [location.pathname, focus, ready]);
}
